import * as Sentry from '@sentry/browser';

import { post } from '../api/api';
import { UserSessionAccessor } from '../authentication/user-provider';

import { isProd } from '../config/config';

const CELLO_ASSET_LINK = isProd
  ? 'assets.cello.so/app/v2/latest/cello.js'
  : 'assets.sandbox.cello.so/app/v2/latest/cello.js';

type CelloTokenResponse = { token: string; celloProductId: string };
// This type is from the Cello documentation
// https://docs.cello.so/docs/javascript-browser#include-cello-js-library
// @ts-expect-error
// eslint-disable-next-line no-multi-assign
window.cello = window.cello || {
  cmd: [] as Array<(cello: CelloClient) => void>,
};

const CELLO_MENU_ATTRIBUTE = 'data-cello';
const CELLO_MENU_VALUE = 'launcher';
const CELLO_MENU_SELECTOR = `[${CELLO_MENU_ATTRIBUTE}=${CELLO_MENU_VALUE}]`;
export const CELLO_MENU_DATA = {
  [CELLO_MENU_ATTRIBUTE]: CELLO_MENU_VALUE,
};

export class CelloController {
  static init = (email: string, firstName: string, lastName: string) => {
    const script = document.createElement('script');
    const body = document.getElementsByTagName('body')[0];
    script.src = `https://${CELLO_ASSET_LINK}`;
    script.async = true;
    script.type = 'module';
    body.appendChild(script);
    script.addEventListener('error', (error) => {
      if (error?.message?.includes('Token not validated')) {
        // no op - we know about these errors, and they don't seem to be causing issues
        return;
      }
      Sentry.captureException(error);
    });
    script.addEventListener('load', () => {
      CelloController.postCelloTokenAndProductId()
        .then((result) => {
          if (!result || !result.token || !result.celloProductId) {
            Sentry.captureMessage(
              `Unable to boot up Cello, missing celloProductId or token (${JSON.stringify(
                result,
              )})`,
            );
            return;
          }

          const { celloProductId, token } = result;

          cello.cmd.push((celloClient: CelloClient) => {
            // https://docs.cello.so/docs/javascript-browser#cellobootoptions
            celloClient.boot({
              productId: celloProductId,
              token,
              // We are using a custom launcher instead of the default widget
              // https://docs.cello.so/docs/cello-custom-launcher#how-to-add-a-custom-launcher
              customLauncherSelector: CELLO_MENU_SELECTOR,
              hideDefaultLauncher: true,
              productUserDetails: {
                firstName,
                lastName,
                email,
              },
              // https://docs.cello.so/docs/user-authentication#ontokenexpiring
              onTokenExpiring: async () => CelloController.updateTokenOnExpire(),
              onTokenExpired: async () => CelloController.updateTokenOnExpire(),
            });
          });
        })
        .catch((e) => Sentry.captureException(e));
    });
  };

  static postCelloTokenRequest = async () => post('/cello/token');

  static postCelloTokenAndProductId = async (): Promise<CelloTokenResponse | undefined> => {
    let newTokenResponse: CelloTokenResponse | undefined;
    try {
      const result = await CelloController.postCelloTokenRequest();

      const { status } = result;
      // If the status is 200, return the result
      // If the status is 500, capture the error
      // We want to ignore 401/403 requests because that means the user is not authenticated and we can't fetch the token
      if (status === 200) {
        newTokenResponse = await result.json();
      }

      if (status === 500) {
        const errorMessage = result ? await result.json() : null;
        const error = new Error(errorMessage?.message || 'Get Cello Token API Request Failed');
        throw error;
      }
    } catch (e) {
      Sentry.captureException(e);
    }
    return newTokenResponse;
  };

  // https://docs.cello.so/docs/user-authentication#updatetokentoken
  static updateTokenOnExpire = async () => {
    const auth0Session = UserSessionAccessor.getSession();

    // If the user is no longer authenticated when the token is expiring, we shutdown Cello
    // https://docs.cello.so/docs/javascript-browser#shutdown
    if (!auth0Session.isAuthenticated) {
      Cello('shutdown');
      return;
    }
    const tokenData = await CelloController.postCelloTokenAndProductId();

    if (!tokenData || !tokenData.token) {
      // Most likely we get into this situation when there is an invalid Auth0 credential header.
      // We were previously logging when this occurred but it seems to be very common and flooded Sentry.
      // If we can't fetch a token for another reason, Cello will continue to call this method until a valid token is updated.
      // So we can return and wait for the next successful attempt.
      return;
    }

    const { token } = tokenData;
    cello.cmd.push(async (cello) => {
      try {
        await cello.updateToken(token);
      } catch (error: any) {
        // JavaScript allows you to do silly things like `throw undefined`, so Typescript makes you do type assertions with the error
        // before you can use it. In this case, we're interacting with a third party API, so we're not totally sure about the typing of
        // the errors (though generally, we assume they are Errors here). As such, we'll take the calculated sin of casting to an any
        // so we can do some reasonable things below
        if (error?.message?.includes('Token not validated')) {
          // no op - we know about these errors, and they don't seem to be causing issues
          return;
        }
        Sentry.withScope((scope) => {
          scope.setExtra('celloResponseIsDefined', !!token);
          scope.setExtra('online', window.navigator.onLine);
          Sentry.captureException(error);
        });
      }
    });
  };
}
